import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Stage, Layer, Image as KImage, Rect, Ellipse, Arrow, Line, Text, Transformer, Group } from 'react-konva';

// ─── helpers ──────────────────────────────────────────────
function useImage(src) {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!src) return;
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    img.src = src;
  }, [src]);
  return image;
}

function fitImageInStage(imgW, imgH, stageW, stageH) {
  const ratio = Math.min(stageW / imgW, stageH / imgH, 1);
  const w = imgW * ratio;
  const h = imgH * ratio;
  return { width: w, height: h, x: (stageW - w) / 2, y: (stageH - h) / 2, scale: ratio };
}

let _uid = 0;
function uid() { return `ann_${Date.now()}_${++_uid}`; }

// ─── Annotation renderers ─────────────────────────────────
function RenderAnnotation({ ann, isSelected, onSelect, onChange }) {
  const shapeRef = useRef(null);
  const trRef = useRef(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    onChange({ ...ann, x: e.target.x(), y: e.target.y() });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    const updated = { ...ann, x: node.x(), y: node.y() };
    if (ann.type === 'rect') {
      updated.width = Math.max(5, node.width() * scaleX);
      updated.height = Math.max(5, node.height() * scaleY);
    } else if (ann.type === 'ellipse') {
      updated.radiusX = Math.max(5, (ann.radiusX || 50) * scaleX);
      updated.radiusY = Math.max(5, (ann.radiusY || 50) * scaleY);
    } else if (ann.type === 'text') {
      updated.fontSize = Math.max(8, (ann.fontSize || 18) * scaleX);
    }
    onChange(updated);
  };

  const commonProps = {
    ref: shapeRef,
    draggable: true,
    onClick: () => onSelect(ann.id),
    onTap: () => onSelect(ann.id),
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
  };

  let shape = null;
  switch (ann.type) {
    case 'rect':
      shape = (
        <Rect
          {...commonProps}
          x={ann.x} y={ann.y}
          width={ann.width || 100} height={ann.height || 60}
          stroke={ann.stroke} strokeWidth={ann.strokeWidth}
          fill={ann.fill === 'transparent' ? undefined : ann.fill}
          cornerRadius={2}
        />
      );
      break;
    case 'ellipse':
      shape = (
        <Ellipse
          {...commonProps}
          x={ann.x} y={ann.y}
          radiusX={ann.radiusX || 50} radiusY={ann.radiusY || 40}
          stroke={ann.stroke} strokeWidth={ann.strokeWidth}
          fill={ann.fill === 'transparent' ? undefined : ann.fill}
        />
      );
      break;
    case 'arrow':
      shape = (
        <Arrow
          {...commonProps}
          x={0} y={0}
          points={ann.points || [ann.x, ann.y, ann.x + 100, ann.y]}
          stroke={ann.stroke} strokeWidth={ann.strokeWidth}
          fill={ann.stroke}
          pointerLength={10} pointerWidth={8}
        />
      );
      break;
    case 'line':
      shape = (
        <Line
          {...commonProps}
          x={0} y={0}
          points={ann.points || [ann.x, ann.y, ann.x + 100, ann.y]}
          stroke={ann.stroke} strokeWidth={ann.strokeWidth}
        />
      );
      break;
    case 'pencil':
      shape = (
        <Line
          {...commonProps}
          x={0} y={0}
          points={ann.points || []}
          stroke={ann.stroke} strokeWidth={ann.strokeWidth}
          tension={0.5}
          lineCap="round" lineJoin="round"
        />
      );
      break;
    case 'text':
      shape = (
        <Text
          {...commonProps}
          x={ann.x} y={ann.y}
          text={ann.text || 'Texte'}
          fontSize={ann.fontSize || 18}
          fill={ann.color || ann.stroke}
          fontStyle="bold"
        />
      );
      break;
    case 'sticker':
      shape = (
        <Text
          {...commonProps}
          x={ann.x} y={ann.y}
          text={ann.emoji || '⚠️'}
          fontSize={ann.fontSize || 36}
        />
      );
      break;
    case 'polygon':
      shape = (
        <Line
          {...commonProps}
          x={0} y={0}
          points={ann.points || []}
          stroke={ann.stroke} strokeWidth={ann.strokeWidth}
          fill={ann.fill === 'transparent' ? undefined : ann.fill}
          closed
        />
      );
      break;
    default:
      return null;
  }

  return (
    <>
      {shape}
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) =>
            newBox.width < 5 || newBox.height < 5 ? oldBox : newBox
          }
          rotateEnabled={false}
        />
      )}
    </>
  );
}

// ─── Crop overlay ─────────────────────────────────────────
function CropOverlay({ stageW, stageH, cropRect, onCropChange }) {
  const handleDragMove = (e) => {
    const { x, y } = e.target.position();
    onCropChange({ ...cropRect, x: Math.max(0, x), y: Math.max(0, y) });
  };

  const handleTransformEnd = (e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    onCropChange({
      x: node.x(),
      y: node.y(),
      width: Math.max(20, node.width() * scaleX),
      height: Math.max(20, node.height() * scaleY),
    });
  };

  const rectRef = useRef(null);
  const trRef = useRef(null);

  useEffect(() => {
    if (trRef.current && rectRef.current) {
      trRef.current.nodes([rectRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, []);

  return (
    <>
      {/* Dim outside crop */}
      <Rect x={0} y={0} width={stageW} height={stageH} fill="rgba(0,0,0,0.5)" listening={false} />
      {/* Crop window */}
      <Rect
        ref={rectRef}
        x={cropRect.x} y={cropRect.y}
        width={cropRect.width} height={cropRect.height}
        fill="transparent" stroke="#3b82f6" strokeWidth={2} dash={[6, 3]}
        draggable
        onDragMove={handleDragMove}
        onTransformEnd={handleTransformEnd}
      />
      <Transformer ref={trRef} rotateEnabled={false}
        boundBoxFunc={(old, nb) => nb.width < 20 || nb.height < 20 ? old : nb}
      />
    </>
  );
}

// ─── Adjust filters ───────────────────────────────────────
function AdjustPanel({ filters, onChange }) {
  const sliders = [
    { key: 'brightness', label: 'Luminosité', min: -100, max: 100 },
    { key: 'contrast', label: 'Contraste', min: -100, max: 100 },
    { key: 'saturation', label: 'Saturation', min: -100, max: 100 },
  ];

  return (
    <div className="absolute left-16 top-2 bg-gray-800/95 rounded-xl shadow-xl p-3 w-52 z-50 backdrop-blur">
      <p className="text-xs font-semibold text-gray-300 mb-2">Réglages image</p>
      {sliders.map(({ key, label, min, max }) => (
        <label key={key} className="block mb-2">
          <span className="text-[10px] text-gray-400">{label}: {filters[key]}</span>
          <input
            type="range" min={min} max={max} value={filters[key]}
            onChange={(e) => onChange({ ...filters, [key]: Number(e.target.value) })}
            className="w-full h-1.5 accent-blue-500"
          />
        </label>
      ))}
      <button
        onClick={() => onChange({ brightness: 0, contrast: 0, saturation: 0 })}
        className="w-full mt-1 text-[10px] py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-300"
      >
        Réinitialiser
      </button>
    </div>
  );
}

// ─── Main Canvas ──────────────────────────────────────────
export default function CanvasStage({
  imageSrc,
  annotations,
  onAnnotationsChange,
  activeTool,
  activeMode,
  style,
  selectedId,
  onSelectedChange,
  stageRef: externalStageRef,
  pendingSticker,
  onStickerPlaced,
}) {
  const containerRef = useRef(null);
  const internalStageRef = useRef(null);
  const stageRef = externalStageRef || internalStageRef;
  const [stageSize, setStageSize] = useState({ width: 600, height: 400 });
  const [drawingPoints, setDrawingPoints] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [filters, setFilters] = useState({ brightness: 0, contrast: 0, saturation: 0 });
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [editingTextId, setEditingTextId] = useState(null);

  const image = useImage(imageSrc);
  const layout = useMemo(() => {
    if (!image) return null;
    return fitImageInStage(image.width, image.height, stageSize.width, stageSize.height);
  }, [image, stageSize]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setStageSize({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Canvas CSS filters for adjust mode
  const cssFilter = useMemo(() => {
    const b = 100 + filters.brightness;
    const c = 100 + filters.contrast;
    const s = 100 + filters.saturation;
    return `brightness(${b}%) contrast(${c}%) saturate(${s}%)`;
  }, [filters]);

  // ─── pointer helpers ───────────────────────────────────
  const getPointerPos = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return null;
    return stage.getPointerPosition();
  }, [stageRef]);

  // ─── Mouse / Touch handlers ────────────────────────────
  const handleMouseDown = useCallback((e) => {
    // Sticker placement
    if (activeMode === 'sticker' && pendingSticker) {
      const pos = getPointerPos();
      if (!pos) return;
      const ann = {
        id: uid(), type: 'sticker', x: pos.x, y: pos.y,
        emoji: pendingSticker.emoji, fontSize: 36,
      };
      onAnnotationsChange((prev) => [...prev, ann]);
      onStickerPlaced?.();
      return;
    }

    if (activeMode === 'crop') return;
    if (activeMode === 'adjust') return;

    const pos = getPointerPos();
    if (!pos) return;

    // Clicking empty space deselects
    if (activeTool === 'select') {
      const clickedOnEmpty = e.target === e.target.getStage() || e.target.getParent() === e.target.getStage()?.children?.[0];
      if (clickedOnEmpty) onSelectedChange(null);
      return;
    }

    if (activeTool === 'eraser') {
      // Eraser: remove clicked shape
      const shape = e.target;
      if (shape !== e.target.getStage()) {
        const id = annotations.find((a) => a.id === selectedId)?.id;
        if (shape.attrs && shape !== e.target.getStage()) {
          // Find annotation matching this shape position
          const clicked = annotations.find(a =>
            Math.abs((a.x || 0) - (shape.x?.() || 0)) < 5 &&
            Math.abs((a.y || 0) - (shape.y?.() || 0)) < 5
          );
          if (clicked) {
            onAnnotationsChange((prev) => prev.filter((a) => a.id !== clicked.id));
          }
        }
      }
      return;
    }

    if (activeTool === 'polygon') {
      setPolygonPoints((prev) => [...prev, pos.x, pos.y]);
      return;
    }

    if (activeTool === 'text') {
      const ann = {
        id: uid(), type: 'text', x: pos.x, y: pos.y,
        text: 'Texte', fontSize: style.strokeWidth * 5 || 18,
        color: style.stroke, stroke: style.stroke,
      };
      onAnnotationsChange((prev) => [...prev, ann]);
      onSelectedChange(ann.id);
      setEditingTextId(ann.id);
      return;
    }

    if (activeTool === 'pencil') {
      setIsDrawing(true);
      setDrawingPoints([pos.x, pos.y]);
      return;
    }

    // Shapes: rect, ellipse, arrow, line
    if (['rect', 'ellipse', 'arrow', 'line'].includes(activeTool)) {
      setIsDrawing(true);
      setDrawingPoints([pos.x, pos.y]);
      return;
    }
  }, [activeMode, activeTool, annotations, getPointerPos, onAnnotationsChange, onSelectedChange, onStickerPlaced, pendingSticker, selectedId, style]);

  const handleMouseMove = useCallback(() => {
    if (!isDrawing) return;
    const pos = getPointerPos();
    if (!pos) return;

    if (activeTool === 'pencil') {
      setDrawingPoints((prev) => [...prev, pos.x, pos.y]);
    }
  }, [activeTool, getPointerPos, isDrawing]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !drawingPoints) return;
    setIsDrawing(false);
    const pos = getPointerPos();
    if (!pos) { setDrawingPoints(null); return; }

    const [sx, sy] = drawingPoints;
    let ann = null;

    switch (activeTool) {
      case 'pencil':
        ann = {
          id: uid(), type: 'pencil', points: drawingPoints,
          stroke: style.stroke, strokeWidth: style.strokeWidth,
        };
        break;
      case 'rect':
        ann = {
          id: uid(), type: 'rect',
          x: Math.min(sx, pos.x), y: Math.min(sy, pos.y),
          width: Math.abs(pos.x - sx), height: Math.abs(pos.y - sy),
          stroke: style.stroke, strokeWidth: style.strokeWidth,
          fill: style.fill,
        };
        break;
      case 'ellipse':
        ann = {
          id: uid(), type: 'ellipse',
          x: (sx + pos.x) / 2, y: (sy + pos.y) / 2,
          radiusX: Math.abs(pos.x - sx) / 2, radiusY: Math.abs(pos.y - sy) / 2,
          stroke: style.stroke, strokeWidth: style.strokeWidth,
          fill: style.fill,
        };
        break;
      case 'arrow':
        ann = {
          id: uid(), type: 'arrow',
          x: 0, y: 0,
          points: [sx, sy, pos.x, pos.y],
          stroke: style.stroke, strokeWidth: style.strokeWidth,
        };
        break;
      case 'line':
        ann = {
          id: uid(), type: 'line',
          x: 0, y: 0,
          points: [sx, sy, pos.x, pos.y],
          stroke: style.stroke, strokeWidth: style.strokeWidth,
        };
        break;
      default:
        break;
    }

    if (ann) {
      onAnnotationsChange((prev) => [...prev, ann]);
      onSelectedChange(ann.id);
    }
    setDrawingPoints(null);
  }, [activeTool, drawingPoints, getPointerPos, isDrawing, onAnnotationsChange, onSelectedChange, style]);

  // Double-click polygon to close
  const handleDblClick = useCallback((e) => {
    if (activeTool === 'polygon' && polygonPoints.length >= 6) {
      const ann = {
        id: uid(), type: 'polygon', x: 0, y: 0,
        points: polygonPoints,
        stroke: style.stroke, strokeWidth: style.strokeWidth,
        fill: style.fill,
      };
      onAnnotationsChange((prev) => [...prev, ann]);
      onSelectedChange(ann.id);
      setPolygonPoints([]);
      return;
    }

    // Double-click text to edit
    const ann = annotations.find((a) => a.id === selectedId && a.type === 'text');
    if (ann) {
      setEditingTextId(ann.id);
    }
  }, [activeTool, annotations, onAnnotationsChange, onSelectedChange, polygonPoints, selectedId, style]);

  // Text inline edit
  useEffect(() => {
    if (!editingTextId) return;
    const ann = annotations.find((a) => a.id === editingTextId);
    if (!ann) return;

    const stage = stageRef.current;
    if (!stage) return;
    const container = stage.container();
    const stageBox = container.getBoundingClientRect();

    const input = document.createElement('input');
    input.type = 'text';
    input.value = ann.text || '';
    input.style.position = 'fixed';
    input.style.left = `${stageBox.left + ann.x}px`;
    input.style.top = `${stageBox.top + ann.y}px`;
    input.style.fontSize = `${ann.fontSize || 18}px`;
    input.style.color = ann.color || '#ef4444';
    input.style.background = 'rgba(0,0,0,0.7)';
    input.style.border = '1px solid #3b82f6';
    input.style.borderRadius = '4px';
    input.style.padding = '2px 6px';
    input.style.outline = 'none';
    input.style.zIndex = '10000';
    input.style.minWidth = '80px';
    document.body.appendChild(input);
    input.focus();
    input.select();

    const finish = () => {
      const newText = input.value.trim() || 'Texte';
      onAnnotationsChange((prev) =>
        prev.map((a) => (a.id === editingTextId ? { ...a, text: newText } : a))
      );
      setEditingTextId(null);
      input.remove();
    };

    input.addEventListener('blur', finish);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') finish();
      if (e.key === 'Escape') { setEditingTextId(null); input.remove(); }
    });

    return () => { input.remove(); };
  }, [editingTextId, annotations, onAnnotationsChange, stageRef]);

  // Crop mode init
  useEffect(() => {
    if (activeMode === 'crop' && layout && !cropRect) {
      setCropRect({
        x: layout.x + layout.width * 0.1,
        y: layout.y + layout.height * 0.1,
        width: layout.width * 0.8,
        height: layout.height * 0.8,
      });
    }
    if (activeMode !== 'crop') {
      setCropRect(null);
    }
  }, [activeMode, layout, cropRect]);

  const handleAnnotationChange = useCallback((updated) => {
    onAnnotationsChange((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  }, [onAnnotationsChange]);

  // Drawing preview (live)
  const drawingPreview = useMemo(() => {
    if (!isDrawing || !drawingPoints) return null;
    const [sx, sy] = drawingPoints;
    if (activeTool === 'pencil') {
      return <Line points={drawingPoints} stroke={style.stroke} strokeWidth={style.strokeWidth} tension={0.5} lineCap="round" lineJoin="round" listening={false} />;
    }
    return null;
  }, [activeTool, drawingPoints, isDrawing, style]);

  // Polygon preview
  const polygonPreview = useMemo(() => {
    if (activeTool !== 'polygon' || polygonPoints.length < 2) return null;
    return <Line points={polygonPoints} stroke={style.stroke} strokeWidth={style.strokeWidth} dash={[4, 4]} listening={false} />;
  }, [activeTool, polygonPoints, style]);

  return (
    <div ref={containerRef} className="flex-1 overflow-hidden relative bg-gray-950" style={{ touchAction: 'none' }}>
      {activeMode === 'adjust' && (
        <AdjustPanel filters={filters} onChange={setFilters} />
      )}

      <div style={{ filter: activeMode === 'adjust' ? cssFilter : undefined }}>
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          onMouseMove={handleMouseMove}
          onTouchMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleMouseUp}
          onDblClick={handleDblClick}
          onDblTap={handleDblClick}
          style={{ cursor: activeTool === 'pencil' ? 'crosshair' : activeTool === 'eraser' ? 'not-allowed' : activeTool === 'select' ? 'default' : 'crosshair' }}
        >
          {/* Background layer */}
          <Layer>
            {image && layout && (
              <KImage
                image={image}
                x={layout.x} y={layout.y}
                width={layout.width} height={layout.height}
                listening={false}
              />
            )}
          </Layer>

          {/* Annotations layer */}
          <Layer>
            {annotations.map((ann) => (
              <RenderAnnotation
                key={ann.id}
                ann={ann}
                isSelected={selectedId === ann.id}
                onSelect={onSelectedChange}
                onChange={handleAnnotationChange}
              />
            ))}
            {drawingPreview}
            {polygonPreview}
          </Layer>

          {/* Crop overlay */}
          {activeMode === 'crop' && cropRect && (
            <Layer>
              <CropOverlay
                stageW={stageSize.width} stageH={stageSize.height}
                cropRect={cropRect} onCropChange={setCropRect}
              />
            </Layer>
          )}
        </Stage>
      </div>

      {/* Crop action buttons */}
      {activeMode === 'crop' && cropRect && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 z-50">
          <button
            onClick={() => {
              // Apply crop — create new cropped image
              if (!stageRef.current || !layout || !image) return;
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const scale = image.width / layout.width;
              const sx = (cropRect.x - layout.x) * scale;
              const sy = (cropRect.y - layout.y) * scale;
              const sw = cropRect.width * scale;
              const sh = cropRect.height * scale;
              canvas.width = sw;
              canvas.height = sh;
              ctx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);
              // Convert to data URL and notify parent
              window.__annotatorCropResult = canvas.toDataURL('image/jpeg', 0.92);
              window.dispatchEvent(new CustomEvent('annotator-crop', { detail: canvas.toDataURL('image/jpeg', 0.92) }));
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-lg"
          >
            ✓ Appliquer
          </button>
          <button
            onClick={() => setCropRect(null)}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 shadow-lg"
          >
            ✗ Annuler
          </button>
        </div>
      )}

      {/* Polygon hint */}
      {activeTool === 'polygon' && polygonPoints.length > 0 && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-gray-800/90 text-white text-xs px-3 py-1.5 rounded-full z-50">
          Cliquez pour ajouter des points · Double-cliquez pour fermer
        </div>
      )}
    </div>
  );
}
